import type { Engine } from '@engine';

/** Which backend to try (`?backend=` in the URL). */
export type BackendOption = 'auto' | 'webgl2' | 'webgpu-fail';

/**
 * `?backend=webgl2` forces WebGL2; `?backend=webgpu-fail` makes WebGPU's startup present
 * probe fail on purpose, to exercise the fallback below on a machine where WebGPU works.
 * Anything else (or nothing) is auto.
 */
export function backendOption(search: string): BackendOption {
  const value = new URLSearchParams(search).get('backend');
  return value === 'webgl2' || value === 'webgpu-fail' ? value : 'auto';
}

/**
 * True for the engine errors that mean "use WebGL2 instead": WebGPU has a device but can't
 * present (`webgpu-cannot-present`), or the WebGPU-only build found no usable adapter
 * (`webgpu-unavailable`).
 */
export function wantsWebGl2(err: unknown): boolean {
  return err instanceof Error && /^webgpu-(cannot-present|unavailable)\b/.test(err.message);
}

/** The two engine builds; see docs/adr/0005-two-engine-builds.md. */
export type EngineBuild = 'webgpu' | 'webgl2';

/** What the browser's WebGPU offers: nothing, only a software (CPU) adapter, or a real GPU. */
export type WebGpuAdapter = 'none' | 'software' | 'hardware';

/**
 * Which build to download first. The WebGPU-only one is a tenth of the size, so take it when
 * the browser has a real WebGPU adapter. Otherwise the WebGPU build would only find that out
 * and send us to the full build anyway: a wasted download (Chrome on Linux exposes
 * `navigator.gpu` with no adapter by default, and so does headless CI).
 */
export function firstBuild(backend: BackendOption, adapter: WebGpuAdapter): EngineBuild {
  return backend !== 'webgl2' && adapter === 'hardware' ? 'webgpu' : 'webgl2';
}

/**
 * Asks for an adapter the way the engine will (low-power, see `Renderer::new`) and reports
 * what came back. One cheap call, made before choosing which build to download. The engine
 * still checks for itself; this only avoids fetching a build that can't be used.
 */
async function probeWebGpu(): Promise<WebGpuAdapter> {
  // The DOM types declare `navigator.gpu`, but Firefox on Linux and older browsers lack it.
  if (!('gpu' in navigator)) return 'none';
  try {
    const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'low-power' });
    if (!adapter) return 'none';
    // `info.isFallbackAdapter` is current; older Chrome had `isFallbackAdapter` on the
    // adapter itself (since removed from the types).
    const legacy = (adapter as GPUAdapter & { isFallbackAdapter?: boolean }).isFallbackAdapter;
    return (adapter.info?.isFallbackAdapter ?? legacy) ? 'software' : 'hardware';
  } catch {
    return 'none';
  }
}

async function createWith(
  build: EngineBuild,
  canvas: HTMLCanvasElement,
  backend: BackendOption,
): Promise<Engine> {
  // Two literal imports, not one computed path: Vite makes each its own chunk, and only the
  // one taken here is fetched.
  const pkg = build === 'webgpu' ? await import('@engine-webgpu') : await import('@engine-webgl2');
  await pkg.default();
  return pkg.Engine.create(canvas, backend);
}

/**
 * A blank copy of `canvas` (same id, classes, size) in its place. Needed because a canvas
 * that has handed out a `webgpu` context returns null for `webgl2` for the rest of its life.
 * Call before anything attaches listeners or observers to the canvas: they stay on the old one.
 */
export function replaceCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const fresh = canvas.cloneNode(false) as HTMLCanvasElement;
  fresh.width = canvas.width;
  fresh.height = canvas.height;
  canvas.replaceWith(fresh);
  return fresh;
}

/**
 * Loads a Wasm engine build and attaches it to a canvas. Dynamic import keeps the engine out
 * of the initial bundle, so the login screen shows instantly.
 *
 * Starts with the small WebGPU-only build where the browser has a real WebGPU adapter. If that can't be
 * used (no or software adapter, or a black canvas that would otherwise report 60 fps),
 * loads the full build on a fresh canvas and runs WebGL2. Returns the canvas the engine
 * ended up on: use that one.
 */
export async function loadEngine(
  canvas: HTMLCanvasElement,
  backend: BackendOption = 'auto',
): Promise<{ engine: Engine; canvas: HTMLCanvasElement; build: EngineBuild }> {
  const build = firstBuild(backend, backend === 'webgl2' ? 'none' : await probeWebGpu());
  try {
    return { engine: await createWith(build, canvas, backend), canvas, build };
  } catch (err) {
    if (build === 'webgl2' || !wantsWebGl2(err)) throw err;
    console.info(`WebGPU not usable here (${(err as Error).message}); loading the WebGL2 engine`);
    // The canvas may have handed out its `webgpu` context already, so use a fresh one.
    const fresh = replaceCanvas(canvas);
    return { engine: await createWith('webgl2', fresh, 'webgl2'), canvas: fresh, build: 'webgl2' };
  }
}
